import type { IntakeCategoryProfile } from "../category-profiles";
import type { ProductOption } from "../types";
import { isUpsellContractType } from "../form-schema";
import { getProductPricingMode, isNumericLikePrice, normalizeProductOptionPricing, type ProductPricingMode } from "./draft-state";
import { fieldValueToString } from "./value-utils";

export type ProductColumnKey = keyof ProductOption;
export type ProductDetailKey = "detailDescription" | "effectDuration" | "recommendedTargets" | "productPrecautions";
export type ProductTableColumn = {
  key: ProductColumnKey;
  label: string;
  width: number;
  placeholder?: string;
  multiline?: boolean;
  numeric?: boolean;
};

export type ProductDetailField = {
  key: ProductDetailKey;
  label: string;
  placeholder: string;
  defaultValue?: string | number | boolean;
};

export const PRODUCT_TABLE_COLUMNS: ProductTableColumn[] = [
  { key: "optionName", label: "옵션명", width: 300, placeholder: "예: 미백 및 광조사 3회" },
  { key: "listPrice", label: "정가", width: 128, placeholder: "600,000" },
  { key: "salePrice", label: "판매가", width: 128, placeholder: "500,000" },
  { key: "freeReservationLabel", label: "무료예약", width: 118 },
  { key: "depositAmount", label: "보증금", width: 128, placeholder: "20,000", numeric: true },
  { key: "settlementPrice", label: "정산가", width: 128, placeholder: "정산가", numeric: true },
  { key: "maxQuantityPerSlot", label: "한타임 최대수량", width: 142, placeholder: "1명" },
  { key: "duration", label: "소요시간", width: 132, placeholder: "약 60분" },
  { key: "includedItems", label: "포함 항목", width: 220, placeholder: "상담, 검사, 시술 등", multiline: true },
  { key: "excludedItems", label: "제외 항목", width: 220, placeholder: "현장 추가 결제 등", multiline: true },
  { key: "recommendationReason", label: "추천 이유", width: 240, placeholder: "외국 여행객에게 적합한 이유", multiline: true },
];

export const PRODUCT_DETAIL_FIELDS: ProductDetailField[] = [
  { key: "detailDescription", label: "상품 상세 설명", placeholder: "예: 치아 표면 착색을 완화하고 밝은 톤을 기대할 수 있는 미백 관리입니다." },
  { key: "effectDuration", label: "효과 지속 기간", placeholder: "예: 개인 생활습관에 따라 6개월~1년 정도" },
  { key: "recommendedTargets", label: "추천 대상", placeholder: "예: 커피/와인 착색이 고민인 고객, 여행 중 빠르게 인상을 개선하고 싶은 고객" },
  { key: "productPrecautions", label: "상품별 주의사항", placeholder: "예: 시술 후 2시간 동안 색소가 강한 음식과 흡연을 피해 주세요." },
];

export function getProductColumnState(_key: ProductColumnKey, _pricingMode: ProductPricingMode): { disabled: boolean } {
  return { disabled: false };
}

export function getProductPasteColumns(_pricingMode: ProductPricingMode, columns: ProductTableColumn[] = PRODUCT_TABLE_COLUMNS): ProductTableColumn[] {
  return columns;
}

function normalizeProductHeader(value: string): string {
  return value.replace(/[\s()[\]{}·/:|_-]/g, "").toLowerCase();
}

export function isProductHeaderRow(cells: string[], columns: ProductTableColumn[], startColumnIndex: number): boolean {
  const expected = columns.slice(startColumnIndex, startColumnIndex + cells.length).map((column) => normalizeProductHeader(column.label));
  const actual = cells.map((cell) => normalizeProductHeader(cell));
  const matchedCount = actual.filter((cell, index) => cell && cell === expected[index]).length;
  return matchedCount >= Math.min(2, expected.length) || (actual[0] !== "" && actual[0] === expected[0]);
}

function productSampleCellValue(column: ProductTableColumn, rowIndex: number, pricingMode: ProductPricingMode, categoryProfile: IntakeCategoryProfile | null, offersConsultationProduct: boolean): string {
  const productNoun = categoryProfile?.productNoun ?? "시술";
  const consultation = categoryProfile?.consultationProduct;
  const consultationRow: Partial<Record<ProductColumnKey, string>> = {
    optionName: consultation?.optionName ?? "무료예약 | 상담 후 시술 결정",
    listPrice: "0",
    salePrice: "0",
    freeReservationLabel: "무료예약",
    depositAmount: "20000",
    maxQuantityPerSlot: "1명",
    duration: consultation?.duration ?? "상담 후 결정",
    includedItems: consultation?.includedItems ?? "현장 상담 후 고객에게 적합한 시술 결정",
    excludedItems: "",
    recommendationReason: consultation?.recommendationReason ?? "어떤 시술을 받을지 정하지 못한 외국인 고객에게 추천",
  };
  const packageRow: Partial<Record<ProductColumnKey, string>> = {
    optionName: `[크리에이트립 단독 패키지] 대표 ${productNoun}`,
    listPrice: "600000",
    salePrice: "500000",
    freeReservationLabel: "무료예약",
    depositAmount: "30000",
    settlementPrice: "400000",
    maxQuantityPerSlot: "1명",
    duration: "약 60분",
    includedItems: `${productNoun} 구성과 기본 제공 항목`,
    excludedItems: categoryProfile?.domain === "beauty" ? "현장 추가 옵션 별도" : "부가세 및 현장 추가 시술 별도",
    recommendationReason: "크리에이트립에서만 예약 가능한 단독 구성",
  };
  const row = (offersConsultationProduct ? [consultationRow, packageRow] : [packageRow])[rowIndex];
  if (!row) return "";
  if (column.key === "freeReservationLabel") return pricingMode === "free" ? row.freeReservationLabel ?? "" : "";
  if (column.key === "depositAmount") return pricingMode === "deposit" ? row.depositAmount ?? "" : "";
  if (column.key === "settlementPrice") return pricingMode === "settlement" ? row.settlementPrice ?? "" : "";
  return row[column.key] ?? "";
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function downloadProductSampleExcel(contractType: string, categoryProfile: IntakeCategoryProfile | null, visibleColumns: ProductTableColumn[] = PRODUCT_TABLE_COLUMNS) {
  const resolvedMode = getProductPricingMode(contractType);
  const pricingMode = resolvedMode === "unselected" ? "free" : resolvedMode;
  const columns = getProductPasteColumns(pricingMode, visibleColumns);
  const offersConsultationProduct = isUpsellContractType(contractType);
  const rows = Array.from({ length: 5 }, (_, rowIndex) => columns.map((column) => productSampleCellValue(column, rowIndex, pricingMode, categoryProfile, offersConsultationProduct)));
  const tableRows = [columns.map((column) => column.label), ...rows]
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");
  const html = `<!doctype html><html><head><meta charset="utf-8" /></head><body><table>${tableRows}</table></body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "onboarding-product-options-sample.xls";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function getProductCellValue(product: ProductOption, key: ProductColumnKey, pricingMode: ProductPricingMode): string {
  if (key === "freeReservationLabel") return pricingMode === "free" ? "무료예약" : "";
  if (key === "depositAmount") {
    const value = fieldValueToString(product.depositAmount);
    const legacy = fieldValueToString(product.depositOrSettlementPrice);
    return value || (pricingMode === "deposit" && isNumericLikePrice(legacy) ? legacy : "");
  }
  if (key === "settlementPrice") {
    const value = fieldValueToString(product.settlementPrice);
    const legacy = fieldValueToString(product.depositOrSettlementPrice);
    return value || (pricingMode === "settlement" && isNumericLikePrice(legacy) ? legacy : "");
  }
  return fieldValueToString(product[key]);
}

export function applyProductCellValue(product: ProductOption, key: ProductColumnKey, value: string, pricingMode: ProductPricingMode): ProductOption {
  const nextProduct = {
    ...product,
    [key]: key === "freeReservationLabel" ? "무료예약" : value,
  } as ProductOption;
  if (key === "depositAmount" && pricingMode === "deposit") nextProduct.depositOrSettlementPrice = value;
  if (key === "settlementPrice" && pricingMode === "settlement") nextProduct.depositOrSettlementPrice = value;
  if (pricingMode === "free") nextProduct.depositOrSettlementPrice = "무료예약";
  return normalizeProductOptionPricing(nextProduct, pricingMode);
}

export function getPricingModeDescription(pricingMode: ProductPricingMode): string {
  if (pricingMode === "free") return "무료예약 상품 기준 — 보증금/정산가 컬럼은 쓰지 않아요";
  if (pricingMode === "deposit") return "보증금 예약 상품 기준 — 보증금 컬럼만 입력해요";
  if (pricingMode === "settlement") return "판매가/정산가 상품 기준 — 정산가 컬럼만 입력해요";
  return "상품 유형에 맞는 가격 컬럼이 자동으로 열려요.";
}
